/**
 * EmployeesPage - Refactored Version
 * Uses shared components: DataTable, StatusBadge, SearchInput
 * Uses shared hooks: useApi, useMutation, useTable
 */
import { useState, useMemo } from 'react';
import { employeeAPI, exportAPI } from '../lib/api';
import ExportButton from '../components/ExportButton';
import { useAuth } from '../context/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { useTable } from '../hooks/useTable';
import { formatDate, formatCurrency, getInitials } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Loader2, Wallet, Eye, Users, Building, UserCheck, UserX, ChevronRight, CheckCircle2, AlertCircle, FileText } from 'lucide-react';

// Shared components
import { DataTable } from '../components/shared/DataTable';
import { StatusBadge, getStatusVariant } from '../components/shared/StatusBadge';
import { SearchInput } from '../components/shared/SearchInput';
import { Pagination } from '../components/shared/Pagination';

// Employee Documents
import EmployeeDocuments from '../components/EmployeeDocuments';

// Sheet components for detail view
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Default form state
const defaultEmployee = {
  employee_id: '',
  name: '',
  email: '',
  department: '',
  designation: '',
  phone: '',
  joining_date: new Date().toISOString().split('T')[0],
  status: 'active',
  employee_type: 'provisional',
  employment_status: 'provisional',
  reporting_manager_id: '',
  basic_salary: 0,
  hra: 0,
  special_allowance: 0,
  pf_applicable: true,
  pt_applicable: true,
  pt_override: '',
  tds_applicable: false,
  bank_account: '',
  bank_name: '',
  bank_ifsc: '',
  pan_number: '',
  date_of_birth: '',
};

// Status configuration for employees
const EMPLOYEE_STATUS = {
  active: { label: 'Active', variant: 'default' },
  inactive: { label: 'Inactive', variant: 'secondary' },
  on_leave: { label: 'On Leave', variant: 'outline' },
  terminated: { label: 'Terminated', variant: 'destructive' },
};

// Employee types
const EMPLOYEE_TYPES = {
  full_time: { label: 'Full Time', variant: 'default', description: 'Regular confirmed employee' },
  part_time: { label: 'Part Time', variant: 'secondary', description: 'Part-time employee' },
  provisional: { label: 'Provisional', variant: 'outline', description: 'On probation' },
  retainer: { label: 'Retainer', variant: 'secondary', description: 'Consultant - No EL' },
  intern: { label: 'Intern', variant: 'outline', description: 'Intern - No EL' },
};

// Employment status
const EMPLOYMENT_STATUS = {
  confirmed: { label: 'Confirmed', variant: 'default', className: 'bg-green-100 text-green-800' },
  provisional: { label: 'Provisional', variant: 'outline', className: 'bg-yellow-100 text-yellow-800' },
};

export default function EmployeesPage() {
  const { isAdmin, isHR, isGuest, isSuperAdmin, user } = useAuth();
  const canEdit = isAdmin || isHR || isSuperAdmin;
  const canEditManager = isSuperAdmin;
  const canConfirm = isAdmin || isHR || isSuperAdmin;

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payrollDialogOpen, setPayrollDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [formData, setFormData] = useState(defaultEmployee);

  // Data fetching with useApi hook
  const { data: employees, loading, refresh } = useApi(employeeAPI.getAll);

  // Create/Update employee mutation
  const { mutate: saveEmployee, loading: submitting } = useMutation(
    (data) => selectedEmployee 
      ? employeeAPI.update(selectedEmployee.id, data)
      : employeeAPI.create(data),
    {
      onSuccess: () => {
        setDialogOpen(false);
        resetForm();
        refresh();
      }
    }
  );

  // Table hook for filtering/sorting
  const {
    items: filteredEmployees,
    search,
    setSearch,
    sort,
    setSort,
    pagination
  } = useTable(employees || [], {
    searchFields: ['name', 'email', 'department', 'designation'],
    defaultSort: { field: 'name', order: 'asc' },
    pageSize: 20
  });

  // Computed stats
  const stats = useMemo(() => ({
    total: (employees || []).length,
    active: (employees || []).filter(e => e.status === 'active').length,
    inactive: (employees || []).filter(e => e.status === 'inactive').length,
    onLeave: (employees || []).filter(e => e.status === 'on_leave').length,
    confirmed: (employees || []).filter(e => e.employment_status === 'confirmed').length,
    provisional: (employees || []).filter(e => e.employment_status !== 'confirmed').length,
  }), [employees]);

  const resetForm = () => {
    setFormData(defaultEmployee);
    setSelectedEmployee(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      joining_date: new Date(formData.joining_date).toISOString(),
      date_of_birth: formData.date_of_birth ? new Date(formData.date_of_birth).toISOString() : null,
      pt_override: formData.pt_override ? parseFloat(formData.pt_override) : null,
    };
    
    try {
      await saveEmployee(payload);
      toast.success(selectedEmployee ? 'Employee updated' : 'Employee added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      ...emp,
      employee_type: emp.employee_type || 'provisional',
      employment_status: emp.employment_status || 'provisional',
      joining_date: new Date(emp.joining_date).toISOString().split('T')[0],
      date_of_birth: emp.date_of_birth ? new Date(emp.date_of_birth).toISOString().split('T')[0] : '',
      bank_name: emp.bank_name || '',
      bank_ifsc: emp.bank_ifsc || '',
      pt_override: emp.pt_override || '',
    });
    setDialogOpen(true);
  };

  const handleViewPayroll = async (emp) => {
    setSelectedEmployee(emp);
    try {
      const now = new Date();
      const res = await employeeAPI.getPayroll(emp.id, now.getMonth() + 1, now.getFullYear());
      setPayrollData(res.data);
      setPayrollDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch payroll');
    }
  };

  const handleConfirmEmployee = async () => {
    if (!selectedEmployee) return;
    
    try {
      const res = await employeeAPI.confirm(selectedEmployee.id);
      toast.success(res.data.message || 'Employee confirmed successfully');
      setConfirmDialogOpen(false);
      setSelectedEmployee(null);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm employee');
    }
  };

  // Open employee detail sheet
  const handleViewDetails = (emp) => {
    setSelectedEmployee(emp);
    setDetailSheetOpen(true);
  };

  // Table column definitions
  const columns = [
    {
      key: 'name',
      label: 'Employee',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary text-sm">
              {getInitials(row.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{row.name}</p>
            <p className="text-sm text-muted-foreground">{row.email}</p>
          </div>
        </div>
      )
    },
    { key: 'department', label: 'Department', sortable: true },
    { key: 'designation', label: 'Designation', sortable: true },
    {
      key: 'employee_type',
      label: 'Type',
      render: (row) => {
        const type = EMPLOYEE_TYPES[row.employee_type] || EMPLOYEE_TYPES.provisional;
        return <Badge variant={type.variant}>{type.label}</Badge>;
      }
    },
    {
      key: 'employment_status',
      label: 'Employment',
      render: (row) => {
        const status = EMPLOYMENT_STATUS[row.employment_status] || EMPLOYMENT_STATUS.provisional;
        return (
          <Badge className={status.className}>
            {row.employment_status === 'confirmed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
            {status.label}
          </Badge>
        );
      }
    },
    {
      key: 'joining_date',
      label: 'Joining Date',
      sortable: true,
      render: (row) => formatDate(row.joining_date)
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <StatusBadge 
          status={row.status} 
          variant={EMPLOYEE_STATUS[row.status]?.variant || 'secondary'}
          label={EMPLOYEE_STATUS[row.status]?.label || row.status}
        />
      )
    },
    {
      key: 'actions',
      label: '',
      className: 'text-right',
      cellClassName: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-2">
          {/* Confirm button - only for provisional employees */}
          {canConfirm && row.employment_status !== 'confirmed' && !['retainer', 'intern'].includes(row.employee_type) && (
            <Button
              variant="ghost"
              size="icon"
              className="text-green-600 hover:text-green-700 hover:bg-green-50"
              onClick={(e) => { 
                e.stopPropagation(); 
                setSelectedEmployee(row);
                setConfirmDialogOpen(true);
              }}
              title="Confirm Employee"
            >
              <CheckCircle2 className="w-4 h-4" />
            </Button>
          )}
          {!isGuest && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); handleViewPayroll(row); }}
              title="View Payroll"
            >
              <Wallet className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); handleViewDetails(row); }}
            title="View Documents"
          >
            <FileText className="w-4 h-4" />
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
              title="Edit Employee"
            >
              <Eye className="w-4 h-4" />
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <div data-testid="employees-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Employees</h1>
          <p className="text-muted-foreground mt-1">Manage your team members</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton 
            exportFn={exportAPI.employees} 
            filename="employees.csv" 
            label="Export"
          />
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full"
                  onClick={resetForm}
                  data-testid="add-employee-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <EmployeeFormDialog
                formData={formData}
                setFormData={setFormData}
                selectedEmployee={selectedEmployee}
                employees={employees || []}
                canEditManager={canEditManager}
                submitting={submitting}
                onSubmit={handleSubmit}
                onClose={() => setDialogOpen(false)}
              />
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <StatsCard icon={Users} label="Total" value={stats.total} color="bg-blue-500" onClick={() => setSearch('')} />
        <StatsCard icon={UserCheck} label="Active" value={stats.active} color="bg-green-500" onClick={() => setSearch('active')} />
        <StatsCard icon={UserX} label="Inactive" value={stats.inactive} color="bg-gray-500" onClick={() => setSearch('inactive')} />
        <StatsCard icon={Building} label="On Leave" value={stats.onLeave} color="bg-yellow-500" onClick={() => setSearch('on_leave')} />
        <StatsCard icon={CheckCircle2} label="Confirmed" value={stats.confirmed} color="bg-emerald-600" onClick={() => setSearch('confirmed')} />
        <StatsCard icon={AlertCircle} label="Provisional" value={stats.provisional} color="bg-orange-500" onClick={() => setSearch('provisional')} />
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search employees..."
          className="w-full md:w-72"
        />
        <p className="text-sm text-muted-foreground">
          Showing {filteredEmployees.length} of {(employees || []).length} employees
        </p>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <DataTable
            data={filteredEmployees}
            columns={columns}
            loading={loading}
            emptyMessage="No employees found"
            sort={sort}
            onSort={setSort}
            testIdPrefix="employee"
          />
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Pagination {...pagination} />
      )}

      {/* Payroll Dialog */}
      <PayrollDialog
        open={payrollDialogOpen}
        onOpenChange={setPayrollDialogOpen}
        employee={selectedEmployee}
        payrollData={payrollData}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Employee</DialogTitle>
            <DialogDescription>
              Are you sure you want to confirm <span className="font-semibold">{selectedEmployee?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <CheckCircle2 className="w-4 h-4 inline mr-2" />
                This action will:
              </p>
              <ul className="mt-2 text-sm text-green-700 list-disc list-inside space-y-1">
                <li>Change employment status to <strong>Confirmed</strong></li>
                <li>Change employee type to <strong>Full Time</strong></li>
                <li>Credit prorated Earned Leave (EL) for remaining quarters</li>
              </ul>
            </div>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone easily. The employee will become eligible for Earned Leave benefits.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmEmployee} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirm Employee
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee Detail Sheet with Documents */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3">
              {selectedEmployee && (
                <>
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(selectedEmployee.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div>{selectedEmployee.name}</div>
                    <div className="text-sm font-normal text-muted-foreground">
                      {selectedEmployee.designation}
                    </div>
                  </div>
                </>
              )}
            </SheetTitle>
            <SheetDescription>
              {selectedEmployee?.email} • {selectedEmployee?.department}
            </SheetDescription>
          </SheetHeader>
          
          {selectedEmployee && (
            <div className="mt-6">
              <Tabs defaultValue="documents">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="info">Info</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Employee ID</p>
                      <p className="font-medium">{selectedEmployee.employee_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium">{selectedEmployee.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Joining Date</p>
                      <p className="font-medium">{selectedEmployee.joining_date ? formatDate(selectedEmployee.joining_date) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <StatusBadge status={selectedEmployee.status} variant={getStatusVariant(selectedEmployee.status)} />
                    </div>
                    <div>
                      <p className="text-muted-foreground">Type</p>
                      <p className="font-medium capitalize">{selectedEmployee.employee_type || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Employment Status</p>
                      <p className="font-medium capitalize">{selectedEmployee.employment_status || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Reporting To</p>
                      <p className="font-medium">{selectedEmployee.reporting_manager_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Work Location</p>
                      <p className="font-medium">{selectedEmployee.work_location || '-'}</p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="documents" className="mt-4">
                  <EmployeeDocuments 
                    employeeId={selectedEmployee.id} 
                    canEdit={canEdit || (user?.email === selectedEmployee.email)}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Stats Card Component
function StatsCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <Card 
      className={onClick ? 'cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all' : ''}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`${color} p-2 rounded-lg text-white`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold">{value}</p>
              <p className="text-sm text-muted-foreground">{label}</p>
            </div>
          </div>
          {onClick && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );
}

// Employee Form Dialog Component
function EmployeeFormDialog({
  formData,
  setFormData,
  selectedEmployee,
  employees,
  canEditManager,
  submitting,
  onSubmit,
  onClose
}) {
  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{selectedEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
        <DialogDescription>Fill in the employee details</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            label="Employee ID"
            value={formData.employee_id}
            onChange={(v) => setFormData({ ...formData, employee_id: v })}
            required
          />
          <FormInput
            label="Full Name"
            value={formData.name}
            onChange={(v) => setFormData({ ...formData, name: v })}
            required
          />
          <FormInput
            label="Email"
            type="email"
            value={formData.email}
            onChange={(v) => setFormData({ ...formData, email: v })}
            required
          />
          <FormInput
            label="Phone"
            value={formData.phone}
            onChange={(v) => setFormData({ ...formData, phone: v })}
          />
          <FormInput
            label="Department"
            value={formData.department}
            onChange={(v) => setFormData({ ...formData, department: v })}
            required
          />
          <FormInput
            label="Designation"
            value={formData.designation}
            onChange={(v) => setFormData({ ...formData, designation: v })}
            required
          />
          <FormInput
            label="Joining Date"
            type="date"
            value={formData.joining_date}
            onChange={(v) => setFormData({ ...formData, joining_date: v })}
            required
          />
          <FormInput
            label="Date of Birth"
            type="date"
            value={formData.date_of_birth}
            onChange={(v) => setFormData({ ...formData, date_of_birth: v })}
          />
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={formData.status}
              onValueChange={(v) => setFormData({ ...formData, status: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EMPLOYEE_STATUS).map(([value, { label }]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Employee Type */}
          <div className="space-y-2">
            <Label>Employee Type</Label>
            <Select
              value={formData.employee_type || 'provisional'}
              onValueChange={(v) => setFormData({ ...formData, employee_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EMPLOYEE_TYPES).map(([value, { label, description }]) => (
                  <SelectItem key={value} value={value}>
                    <div>
                      <span>{label}</span>
                      <span className="text-xs text-muted-foreground ml-2">({description})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Employment Status */}
          <div className="space-y-2">
            <Label>Employment Status</Label>
            <Select
              value={formData.employment_status || 'provisional'}
              onValueChange={(v) => setFormData({ ...formData, employment_status: v })}
              disabled={selectedEmployee?.employment_status === 'confirmed'}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EMPLOYMENT_STATUS).map(([value, { label }]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedEmployee?.employment_status === 'confirmed' && (
              <p className="text-xs text-muted-foreground">Already confirmed - cannot be changed</p>
            )}
          </div>
          
          {/* Manager Field */}
          <div className="space-y-2 col-span-2">
            <Label>
              Reporting Manager 
              {!canEditManager && (
                <span className="text-xs text-muted-foreground ml-1">
                  (View only - Contact Super Admin to change)
                </span>
              )}
            </Label>
            {canEditManager ? (
              <Select
                value={formData.reporting_manager_id || 'none'}
                onValueChange={(v) => setFormData({ 
                  ...formData, 
                  reporting_manager_id: v === 'none' ? '' : v 
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reporting manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {employees
                    .filter(emp => emp.id !== selectedEmployee?.id && emp.status === 'active')
                    .map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.designation})
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={
                  formData.reporting_manager_id
                    ? employees.find(e => e.id === formData.reporting_manager_id)?.name || 'Unknown'
                    : 'No Manager Assigned'
                }
                disabled
                className="bg-muted"
              />
            )}
          </div>
        </div>

        {/* Payroll Section */}
        <div className="border-t pt-4 mt-4">
          <h4 className="font-semibold mb-4">Payroll Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              label="Basic Salary (₹)"
              type="number"
              value={formData.basic_salary}
              onChange={(v) => setFormData({ ...formData, basic_salary: parseFloat(v) || 0 })}
            />
            <FormInput
              label="HRA (₹)"
              type="number"
              value={formData.hra}
              onChange={(v) => setFormData({ ...formData, hra: parseFloat(v) || 0 })}
            />
            <FormInput
              label="Special Allowance (₹)"
              type="number"
              value={formData.special_allowance}
              onChange={(v) => setFormData({ ...formData, special_allowance: parseFloat(v) || 0 })}
            />
            <FormInput
              label="PAN Number"
              value={formData.pan_number}
              onChange={(v) => setFormData({ ...formData, pan_number: v })}
            />
          </div>
          
          {/* Bank Details Section */}
          <div className="mt-4 p-4 border rounded-lg bg-muted/30">
            <Label className="text-base font-semibold mb-3 block">Bank Details</Label>
            <div className="grid grid-cols-3 gap-4">
              <FormInput
                label="Bank Name"
                value={formData.bank_name || ''}
                onChange={(v) => setFormData({ ...formData, bank_name: v })}
                placeholder="e.g., State Bank of India"
              />
              <FormInput
                label="Account Number"
                value={formData.bank_account || ''}
                onChange={(v) => setFormData({ ...formData, bank_account: v })}
                placeholder="Account number"
              />
              <FormInput
                label="IFSC Code"
                value={formData.bank_ifsc || ''}
                onChange={(v) => setFormData({ ...formData, bank_ifsc: v.toUpperCase() })}
                placeholder="e.g., SBIN0001234"
                maxLength={11}
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-6 mt-4">
            <SwitchField
              label="PF Applicable"
              checked={formData.pf_applicable}
              onChange={(v) => setFormData({ ...formData, pf_applicable: v })}
            />
            <SwitchField
              label="PT Applicable"
              checked={formData.pt_applicable}
              onChange={(v) => setFormData({ ...formData, pt_applicable: v })}
            />
            <SwitchField
              label="TDS Applicable"
              checked={formData.tds_applicable}
              onChange={(v) => setFormData({ ...formData, tds_applicable: v })}
            />
          </div>
          
          {/* PT Override - Only visible when PT is applicable */}
          {formData.pt_applicable && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <div className="space-y-2">
                <Label>PT Override Amount (₹) <span className="text-xs text-muted-foreground">(Leave blank to auto-calculate)</span></Label>
                <Input
                  type="number"
                  placeholder="Auto-calculated based on salary slab"
                  value={formData.pt_override || ''}
                  onChange={(e) => setFormData({ ...formData, pt_override: e.target.value ? parseFloat(e.target.value) : null })}
                  className="max-w-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Standard slabs: ₹0 (≤15k), ₹150 (15k-25k), ₹200 (&gt;25k). Override if needed.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}

// Payroll Dialog Component
function PayrollDialog({ open, onOpenChange, employee, payrollData }) {
  if (!payrollData) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Payroll Details</DialogTitle>
          <DialogDescription>
            {employee?.name} - {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <PayrollCard label="Basic Salary" value={payrollData.basic_salary} />
            <PayrollCard label="HRA" value={payrollData.hra} />
            <PayrollCard label="Special Allowance" value={payrollData.special_allowance} />
            <PayrollCard label="Gross Salary" value={payrollData.gross_salary} highlight="green" />
          </div>
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">Deductions</h4>
            <div className="space-y-2">
              <DeductionRow label="PF (12%)" value={payrollData.pf_deduction} />
              <DeductionRow label="Professional Tax" value={payrollData.pt_deduction} />
              <DeductionRow label="TDS" value={payrollData.tds_deduction} />
            </div>
          </div>
          <div className="border-t pt-4">
            <div className="flex justify-between items-center p-4 rounded-lg bg-primary/10">
              <span className="font-semibold text-primary">Net Salary</span>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(payrollData.net_salary)}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper Components
function FormInput({ label, type = 'text', value, onChange, required = false, ...props }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        {...props}
      />
    </div>
  );
}

function SwitchField({ label, checked, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <Switch checked={checked} onCheckedChange={onChange} />
      <Label>{label}</Label>
    </div>
  );
}

function PayrollCard({ label, value, highlight }) {
  const bgClass = highlight === 'green' ? 'bg-green-50' : 'bg-muted/50';
  const textClass = highlight === 'green' ? 'text-green-700' : '';
  
  return (
    <div className={`p-4 rounded-lg ${bgClass}`}>
      <p className={`text-sm ${highlight === 'green' ? 'text-green-700' : 'text-muted-foreground'}`}>
        {label}
      </p>
      <p className={`text-lg font-semibold ${textClass}`}>
        {formatCurrency(value)}
      </p>
    </div>
  );
}

function DeductionRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-red-600">-{formatCurrency(value)}</span>
    </div>
  );
}
